/**
 * @file Boutons des 3 buffs x3 (Money/Power/Speed) : temps restant, activation gratuite periodique ou payante en gemmes.
 */
import type { BuffId } from '@config/types';
import { formatBig, formatDuration } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { send } from '../core/feedback';
import { currencyIcon } from '../core/icons';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import type { UiContext } from '../UiContext';

const ICONS: Record<BuffId, UiIconId> = { moneyBuff: 'money', powerBuff: 'muscle', speedBuff: 'bolt' };

interface BuffView {
  root: HTMLElement;
  sub: HTMLElement;
  subIco: SVGSVGElement;
  cost: HTMLElement;
}

export class BuffBar extends Component {
  private readonly views = new Map<BuffId, BuffView>();

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'buffbar');
  }

  protected build(): void {
    const { i18n, config } = this.ctx;
    for (const spec of config.balance.buffs) {
      const id = spec.id;
      const subIco = uiIcon('hourglass', 'ui-ico buff-sub-ico is-hidden');
      const sub = h('span', { class: 'buff-sub-text' });
      const cost = h('span', { class: 'buff-cost' }, currencyIcon('dia'), h('b', {}, formatBig(spec.cost.dia ?? 0)));
      const root = h(
        'button',
        {
          class: `buff buff-${id}`,
          type: 'button',
          'data-ui-block': true,
          'data-fast': true,
          'data-tutorial': `buff-${id}`,
          onclick: () => {
            const free = this.ctx.sim.query.buff(id).freeIn <= 0;
            void send(this.ctx, { type: 'buff.activate', id, useFree: free });
          },
        },
        h('span', { class: 'buff-icon' }, uiIcon(ICONS[id])),
        h('span', { class: 'buff-text' }, h('span', { class: 'buff-name' }, i18n.t(`buff.${id}`)), h('span', { class: 'buff-sub' }, subIco, sub)),
        cost,
      );
      this.views.set(id, { root, sub, subIco, cost });
      this.el.append(root);
    }
    this.update(0);
  }

  override update(_dt: number): void {
    const { query } = this.ctx.sim;
    const unlocked = query.feature('buffs');
    toggleClass(this.el, 'is-hidden', !unlocked);
    if (!unlocked) return;
    const { i18n } = this.ctx;
    for (const [id, view] of this.views) {
      const b = query.buff(id);
      const active = b.seconds > 0;
      const freeReady = b.freeIn <= 0;
      toggleClass(view.root, 'active', active);
      toggleClass(view.root, 'super', b.superActive);
      toggleClass(view.root, 'free', freeReady);
      toggleClass(view.cost, 'is-hidden', freeReady);
      const cooling = !active && !freeReady && Number.isFinite(b.freeIn);
      view.subIco.classList.toggle('is-hidden', !cooling);
      if (active) setText(view.sub, formatDuration(b.seconds));
      else if (freeReady) setText(view.sub, i18n.t('common.free'));
      else setText(view.sub, cooling ? formatDuration(b.freeIn) : i18n.t('buff.activate')); // court : le bouton ne fait que ~58 px de large
    }
  }
}
